import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { MapPin, Building2, Users, Eye, EyeOff, Pencil, Trash2 } from "lucide-react";

interface JobListing {
  id: string;
  title: string;
  company_name: string;
  location: string;
  employment_type: string;
  status: string;
  created_at: string;
  salary_min: number | null;
  salary_max: number | null;
  applications_count?: number;
}

export default function EmployerJobListings() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<JobListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("all");

  useEffect(() => {
    fetchJobListings();
  }, []);

  const fetchJobListings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("job_postings")
        .select(`
          *,
          applications:job_applications(count)
        `)
        .eq("employer_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      setJobs((data as any) || []);
    } catch (error: any) {
      console.error("Fetch error:", error);
      toast({
        title: "Error Loading Job Listings",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleJobStatus = async (jobId: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === "active" ? "inactive" : "active";

      const { error } = await supabase
        .from("job_postings")
        .update({ status: newStatus })
        .eq("id", jobId);

      if (error) throw error;

      toast({
        title: `Job ${newStatus === "active" ? "Activated" : "Deactivated"}`,
        description: `The job posting is now ${newStatus}.`,
      });

      fetchJobListings();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const deleteJob = async (jobId: string) => {
    if (!confirm("Are you sure you want to delete this job posting?")) return;

    try {
      const { error } = await supabase
        .from("job_postings")
        .delete()
        .eq("id", jobId);

      if (error) throw error;

      toast({
        title: "Job Deleted",
        description: "The job posting has been removed.",
      });

      fetchJobListings();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "inactive":
        return "bg-gray-100 text-gray-800";
      case "closed":
        return "bg-red-100 text-red-800";
      default:
        return "bg-blue-100 text-blue-800";
    }
  };

  const filteredJobs = jobs.filter((job) => {
    if (filter === "all") return true;
    return job.status === filter;
  });

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading...</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 bg-muted animate-pulse rounded" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>My Job Postings</CardTitle>
              <CardDescription>Manage your active and inactive job listings</CardDescription>
            </div>
            <Button onClick={() => navigate("/employer/post-job")}>
              Post New Job
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-6">
            <Button
              variant={filter === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter("all")}
            >
              All ({jobs.length})
            </Button>
            <Button
              variant={filter === "active" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter("active")}
            >
              Active ({jobs.filter(j => j.status === "active").length})
            </Button>
            <Button
              variant={filter === "inactive" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter("inactive")}
            >
              Inactive ({jobs.filter(j => j.status === "inactive").length})
            </Button>
          </div>

          {filteredJobs.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No job postings found</h3>
              <p className="text-muted-foreground mb-4">
                {filter === "all"
                  ? "Create your first job posting to start receiving applications."
                  : `No ${filter} job postings at the moment.`}
              </p>
              <Button onClick={() => navigate("/employer/post-job")}>
                Post Your First Job
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredJobs.map((job) => (
                <Card key={job.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-semibold">{job.title}</h3>
                          <Badge className={getStatusColor(job.status)}>
                            {job.status}
                          </Badge>
                        </div>

                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-3">
                          <span className="flex items-center gap-1">
                            <Building2 className="h-4 w-4" />
                            {job.company_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {job.location}
                          </span>
                          {job.applications_count !== undefined && (
                            <span className="flex items-center gap-1">
                              <Users className="h-4 w-4" />
                              {job.applications_count || 0} applications
                            </span>
                          )}
                        </div>

                        {(job.salary_min || job.salary_max) && (
                          <p className="text-sm font-medium text-primary">
                            £{job.salary_min?.toLocaleString() || "N/A"} - £{job.salary_max?.toLocaleString() || "N/A"}
                          </p>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => toggleJobStatus(job.id, job.status)}
                          title={job.status === "active" ? "Deactivate" : "Activate"}
                        >
                          {job.status === "active" ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => navigate(`/employer/post-job?edit=${job.id}`)}
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => deleteJob(job.id)}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
